import { type ReactNode } from 'react';
import {
  ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Tooltip, Legend
} from 'recharts';
import { useChartTheme, getDefaultSeriesColors } from './theme.js';
import { ChartTooltip } from './ChartTooltip.js';

export interface PieChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  /** Pie chart radius. */
  size?: number;
  h?: number | string;
  w?: number | string;
  withTooltip?: boolean;
  withLegend?: boolean;
  withLabels?: boolean;
  /** Format value labels and tooltip values. */
  valueFormatter?: (value: number) => string;
  /** Convert to donut by setting an inner radius (0..1 ratio). */
  innerRadiusRatio?: number;
  /** Render a center label/value (donut style). */
  centerLabel?: ReactNode;
  className?: string;
}

/** Pie / donut chart. Pass `innerRadiusRatio` > 0 (e.g. 0.5) for a donut. */
export function PieChart(props: PieChartProps): ReactNode {
  const {
    data, size = 220, h, w = '100%',
    withTooltip = true, withLegend = true, withLabels,
    valueFormatter, innerRadiusRatio = 0, centerLabel, className
  } = props;
  const theme = useChartTheme();
  const palette = [theme.primary, theme.secondary, theme.tertiary, ...getDefaultSeriesColors()];
  const outer = size / 2;
  const inner = outer * innerRadiusRatio;

  return (
    <div className={['rr-chart', 'rr-chart--pie', className].filter(Boolean).join(' ')} style={{ width: w, height: h ?? size, position: 'relative' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RPieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%" cy="50%"
            innerRadius={inner}
            outerRadius={outer}
            paddingAngle={inner ? 1 : 0}
            label={(withLabels ? (v: { name: string; value: number }) => `${v.name}: ${valueFormatter ? valueFormatter(v.value) : v.value}` : false) as never}
            isAnimationActive
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color ?? palette[i % palette.length]} />
            ))}
          </Pie>
          {withTooltip ? <Tooltip content={<ChartTooltip formatValue={valueFormatter} />} /> : null}
          {withLegend ? <Legend wrapperStyle={{ fontFamily: 'var(--rr-font-system)', fontSize: 12, color: theme.axisLabel }} /> : null}
        </RPieChart>
      </ResponsiveContainer>
      {centerLabel && innerRadiusRatio > 0 ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            textAlign: 'center'
          }}
        >
          {centerLabel}
        </div>
      ) : null}
    </div>
  );
}

/** Pre-configured donut (inner radius 0.55). */
export function DonutChart(props: Omit<PieChartProps, 'innerRadiusRatio'> & { innerRadiusRatio?: number }): ReactNode {
  return <PieChart innerRadiusRatio={props.innerRadiusRatio ?? 0.55} {...props} />;
}
